import React from "react"
import { FormContainer, Worksheet, LabeledInput, CheckboxYesNo } from "uikit"
import { Dropdown, Input } from "semantic-ui-react"
import { useStore } from "store"
import { Footer } from "./Footer"
import { Link } from "react-router-dom"
import { AddCarModal } from "components/accounts/AddCarModal"

const CarForm = ({
  data: {
    fields: {
      description,
      start_year,
      amount,
      use_financing,
      credit_rating,
      property_id,
      min_period_years
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  currentYear,
  yearOptions,
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  isNew,
  monthOptions,
  addCarProperty,
  propertyOptions,
  creditRatingOptions
}) => {
  const addModalOptions = {
    values: { description: "" }
  }
  let startingYearPeriod = 3
  const periodYearOptions = Array(10)
    .fill()
    .map((_, i) => ({
      value: startingYearPeriod + i + "",
      text: startingYearPeriod + i
    }))

  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row label="What do you want to call this goal?" paddedField>
          <Worksheet.Field error={description.error}>
            <Input {...description} autoComplete="off" />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="What year do you want to buy the car?"
          paddedField
        >
          <Worksheet.Field error={start_year.error}>
            <Dropdown
              {...start_year}
              search
              selection
              options={yearOptions}
              fluid
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row label="How much do you want to spend?" paddedField>
          <Worksheet.Field error={amount.error}>
            <LabeledInput
              {...amount}
              label=""
              iconText="$"
              iconPosition="left"
              placeholder="0"
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Do you want to consider financing the purchase?"
          paddedField
        >
          <Worksheet.Field>
            <CheckboxYesNo
              fluid
              checked={use_financing.value === "true"}
              {...use_financing}
            />
          </Worksheet.Field>
          {use_financing.value === "true" ? (
            <Worksheet.Row label="What is your credit rating?" paddedField>
              <Worksheet.Field error={credit_rating.error}>
                <Dropdown
                  fluid
                  {...credit_rating}
                  search
                  selection
                  options={creditRatingOptions}
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>

        <Worksheet.Row
          label="Which car do you want to trade-in or sell?"
          paddedField
        >
          <Worksheet.Field error={property_id.error}>
            {propertyOptions && propertyOptions.length > 0 ? (
              <Dropdown
                fluid
                selection
                options={[
                  ...propertyOptions,
                  { text: "Add Car...", value: "__ADD__" }
                ]}
                {...property_id}
              />
            ) : (
              <Link
                to={`/organize/accounts+property/properties/new/car`}
                className="navlink"
              >
                Add car to sell in Accounts+Property
              </Link>
            )}
            {property_id.value === "__ADD__" && (
              <AddCarModal
                submit={propertyValues =>
                  addCarProperty(propertyValues).then(({ propertyId }) =>
                    property_id.onChange(null, { value: propertyId })
                  )
                }
                onCancel={() => property_id.onChange(null, { value: null })}
                {...addModalOptions}
              />
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="How often do you want to replace this car?"
          paddedField
        >
          <Worksheet.Field error={min_period_years.error}>
            <Dropdown
              {...min_period_years}
              search
              selection
              options={periodYearOptions}
              fluid
            />
          </Worksheet.Field>
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        baseUrl={baseUrl}
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        isPending={isPending}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Goal"
        error={error}
      />
    </Worksheet>
  )
}
const CarFormContainer = FormContainer(
  {
    description: {
      type: "string"
    },
    start_year: {
      type: "unit",
      useGrouping: false,
      minValue: new Date().getFullYear(),
      maxValue: new Date().getFullYear() + 25
    },
    amount: {
      type: "unit",
      minValue: 10000
    },
    use_financing: {
      type: "boolean"
    },
    credit_rating: {
      type: "string",
      defaultValue: "good"
    },
    property_id: {
      type: "string",
      required: false
    },
    min_period_years: {
      type: "integer"
    },
    type: {
      type: "string",
      defaultValue: "car"
    }
  },
  (fields, ...form) => {}
)(CarForm)

const Car = ({ household, goal, scenario, ...formProps }) => {
  const [people] = useStore("householdPeople", household.id)
  const [
    properties,
    { addGoal, updatePerson, updateGoal, deleteGoal, addProperty }
  ] = useStore("householdProperties", household.id)

  let values = {}
  const isNew = goal === null
  let currentYear = new Date().getFullYear()
  const yearOptions = Array(26)
    .fill()
    .map((_, i) => ({
      value: currentYear + i + "",
      text: currentYear + i
    }))
  const creditRatingOptions = ["Fair", "Good", "Excellent"].map(label => ({
    text: label,
    value: label.toLowerCase()
  }))
  const propertyOptions = Object.values(properties)
    .filter(property => property.type === "car")
    .map(({ description: text, id: value }) => ({
      text,
      value
    }))
  propertyOptions.push({
    value: null,
    text: "None"
  })

  const client = Object.values(people).find(person => person.role === "client")

  if (!isNew) {
    const propertyData = propertyOptions.find(
      property => goal.car_to_sell && property.value === goal.car_to_sell
    )
    const car_to_sell = propertyData ? goal.car_to_sell : null
    values = { ...goal, car_to_sell }

    const credit_rating =
      client && client.credit_rating ? client.credit_rating : ""
    Object.assign(values, {
      credit_rating
    })
  }

  const submit = isNew
    ? async ({ credit_rating, ...goalData }) => {
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...goalData
        })
        await updatePerson({
          id: client.id,
          credit_rating
        })
      }
    : async ({ credit_rating, ...goalData }) => {
        await updateGoal({ id: goal.id, ...goalData })
        await updatePerson({
          id: client.id,
          credit_rating
        })
      }
  const onDelete = async () => {
    await deleteGoal({ id: goal.id, scenario_id: scenario.id })
    return true
  }

  const addCarProperty = async values =>
    addProperty({
      household_id: household.id,
      type: "car",
      ...values
    })

  return (
    <CarFormContainer
      {...{
        submit,
        onDelete,
        values,
        yearOptions,
        currentYear,
        isNew,
        addCarProperty,
        propertyOptions,
        creditRatingOptions,
        ...formProps
      }}
    />
  )
}

export default Car
